import React, { useEffect, useMemo, useState } from 'react';
import { FileText, Image as ImageIcon, X } from 'lucide-react';
import { resolvePreviewUrl } from '../utils/images';

export const FINANCE_COUNTRIES = ['Malaysia', 'Sri Lanka'];
export const FINANCE_PAYMENT_METHODS = [
  'Cash',
  'Bank transfer',
  'Credit / debit card',
  'Online payment',
  'Cheque',
];

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export function emptyTransactionForm() {
  return {
    country: 'Malaysia',
    categoryId: '',
    subcategoryId: '',
    paymentMethod: '',
    amount: '',
    transactionDate: todayISO(),
    reference: '',
    description: '',
    receiptUrl: '',
    receiptFile: null,
  };
}

export function transactionToFormValues(row, categories, type) {
  if (!row) return emptyTransactionForm();

  const cat = row.categoryId;
  const subId = cat?._id || cat;
  let categoryId = '';
  let subcategoryId = String(subId || '');

  if (cat?.parentId) {
    categoryId = String(cat.parentId);
  } else {
    const parents = categories.filter((c) => c.type === type);
    for (const p of parents) {
      if ((p.children || []).some((ch) => String(ch._id) === String(subId))) {
        categoryId = String(p._id);
        break;
      }
    }
  }

  const d = row.transactionDate ? new Date(row.transactionDate) : new Date();
  const transactionDate = Number.isNaN(d.getTime()) ? todayISO() : d.toISOString().slice(0, 10);

  return {
    country: row.country || 'Malaysia',
    categoryId,
    subcategoryId,
    paymentMethod: row.paymentMethod || '',
    amount: row.amount != null ? String(row.amount) : '',
    transactionDate,
    reference: row.reference || '',
    description: row.description || '',
    receiptUrl: row.receiptUrl || '',
    receiptFile: null,
  };
}

function RequiredLabel({ children }) {
  return (
    <>
      {children}
      <span className="jet-finance-req" aria-hidden> *</span>
    </>
  );
}

export default function FinanceTransactionForm({
  type,
  title,
  categories,
  initialValues,
  isEdit = false,
  onSubmit,
  onCancel,
  onError,
  saving,
  apiBase,
  authToken,
}) {
  const isExpense = type === 'expense';
  const formTitle = title || (isEdit
    ? (isExpense ? 'Edit expense' : 'Edit income')
    : (isExpense ? 'Expense Information' : 'Transaction Details'));

  const [form, setForm] = useState(() => initialValues || emptyTransactionForm());

  useEffect(() => {
    setForm(initialValues || emptyTransactionForm());
  }, [initialValues]);

  const filteredCategories = useMemo(
    () => categories.filter((c) => c.type === type),
    [categories, type],
  );

  const selectedParent = useMemo(
    () => filteredCategories.find((c) => String(c._id) === form.categoryId),
    [filteredCategories, form.categoryId],
  );

  const receiptPreviewSrc = useMemo(() => {
    if (form.receiptFile) return resolvePreviewUrl(form.receiptFile, apiBase);
    if (form.receiptUrl) return resolvePreviewUrl(form.receiptUrl, apiBase);
    return '';
  }, [form.receiptFile, form.receiptUrl, apiBase]);

  const isPdfReceipt = useMemo(() => {
    if (form.receiptFile) {
      return form.receiptFile.type === 'application/pdf'
        || String(form.receiptFile.name || '').toLowerCase().endsWith('.pdf');
    }
    return String(form.receiptUrl || '').toLowerCase().endsWith('.pdf');
  }, [form.receiptFile, form.receiptUrl]);

  useEffect(() => {
    if (!form.receiptFile || typeof form.receiptFile === 'string') return undefined;
    const blobUrl = resolvePreviewUrl(form.receiptFile, apiBase);
    return () => {
      if (blobUrl.startsWith('blob:')) URL.revokeObjectURL(blobUrl);
    };
  }, [form.receiptFile, apiBase]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subcategoryId) {
      onError?.('Please select a sub category');
      return;
    }
    try {
      let receiptUrl = form.receiptUrl || '';
      if (form.receiptFile && apiBase && authToken) {
        try {
          const fd = new FormData();
          fd.append('receipt', form.receiptFile);
          const upRes = await fetch(`${apiBase}/api/finance/upload-receipt`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${authToken}` },
            body: fd,
          });
          const upJson = await upRes.json().catch(() => ({}));
          if (!upRes.ok) {
            throw new Error(upJson.message || `Receipt upload failed (${upRes.status})`);
          }
          receiptUrl = upJson.url || '';
        } catch (uploadErr) {
          onError?.(`${uploadErr.message}. Saving without receipt — restart the backend if uploads keep failing.`);
          receiptUrl = '';
        }
      }
      await onSubmit({
        categoryId: form.subcategoryId,
        amount: Number.parseFloat(form.amount),
        transactionDate: form.transactionDate,
        description: form.description.trim(),
        reference: isExpense ? '' : form.reference.trim(),
        paymentMethod: isExpense ? '' : form.paymentMethod,
        country: form.country,
        receiptUrl,
      });
      if (!isEdit) setForm(emptyTransactionForm());
    } catch (err) {
      onError?.(err.message || 'Failed to save');
    }
  };

  return (
    <form className="jet-finance-txn-form" onSubmit={handleSubmit}>
      <div className="jet-finance-txn-form-head">
        <h3>{formTitle}</h3>
        <button type="button" className="jet-btn jet-btn-secondary jet-btn-icon" onClick={onCancel} aria-label="Close">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="jet-finance-txn-grid">
        <label>
          <RequiredLabel>Country</RequiredLabel>
          <select
            className="form-input"
            value={form.country}
            onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
            required
          >
            <option value="">Select Country</option>
            {FINANCE_COUNTRIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </label>

        <label>
          <RequiredLabel>Category</RequiredLabel>
          <select
            className="form-input"
            value={form.categoryId}
            onChange={(e) => setForm((f) => ({ ...f, categoryId: e.target.value, subcategoryId: '' }))}
            required
          >
            <option value="">Select Category</option>
            {filteredCategories.map((c) => (
              <option key={c._id} value={c._id}>{c.name}</option>
            ))}
          </select>
        </label>

        <label>
          <RequiredLabel>Sub Category</RequiredLabel>
          <select
            className="form-input"
            value={form.subcategoryId}
            onChange={(e) => setForm((f) => ({ ...f, subcategoryId: e.target.value }))}
            required
            disabled={!selectedParent?.children?.length}
          >
            <option value="">Select Sub Category</option>
            {(selectedParent?.children || []).map((sub) => (
              <option key={sub._id} value={sub._id}>{sub.name}</option>
            ))}
          </select>
        </label>

        <label>
          <RequiredLabel>{isExpense ? 'Amount (RM)' : 'Amount'}</RequiredLabel>
          <input
            type="number"
            className="form-input"
            min="0.01"
            step="0.01"
            placeholder="0.00"
            value={form.amount}
            onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
            required
          />
        </label>

        {!isExpense ? (
          <label>
            <RequiredLabel>Payment Method</RequiredLabel>
            <select
              className="form-input"
              value={form.paymentMethod}
              onChange={(e) => setForm((f) => ({ ...f, paymentMethod: e.target.value }))}
              required
            >
              <option value="">Select Method</option>
              {FINANCE_PAYMENT_METHODS.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </label>
        ) : null}

        <label className={isExpense ? 'jet-finance-txn-full' : undefined}>
          <RequiredLabel>{isExpense ? 'Expense Date' : 'Transaction Date'}</RequiredLabel>
          <input
            type="date"
            className="form-input"
            value={form.transactionDate}
            onChange={(e) => setForm((f) => ({ ...f, transactionDate: e.target.value }))}
            required
          />
        </label>

        {!isExpense ? (
          <label className="jet-finance-txn-full">
            Reference Number
            <input
              type="text"
              className="form-input"
              placeholder="Ref #"
              value={form.reference}
              onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))}
            />
          </label>
        ) : null}

        <label className="jet-finance-txn-full">
          <RequiredLabel>Description</RequiredLabel>
          <textarea
            className="form-input jet-finance-textarea"
            rows={3}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            required
          />
        </label>
      </div>

      <div className="jet-finance-receipt-block">
        <h4>Receipt Upload <span className="jet-finance-opt">(Optional)</span></h4>
        {(form.receiptUrl || form.receiptFile) ? (
          <div className="jet-finance-receipt-current">
            <span className="jet-finance-receipt-current-label">Current receipt</span>
            <div className="jet-crm-upload__row">
              <div className="jet-crm-upload__preview jet-finance-receipt-preview">
                {isPdfReceipt && receiptPreviewSrc ? (
                  <a href={receiptPreviewSrc} target="_blank" rel="noreferrer" className="jet-finance-receipt-pdf">
                    <FileText className="w-8 h-8" aria-hidden />
                    <span>View PDF</span>
                  </a>
                ) : receiptPreviewSrc ? (
                  <a href={receiptPreviewSrc} target="_blank" rel="noreferrer" title="Open receipt">
                    <img src={receiptPreviewSrc} alt="Receipt preview" />
                  </a>
                ) : (
                  <ImageIcon className="w-5 h-5" style={{ opacity: 0.35 }} aria-hidden />
                )}
              </div>
            </div>
          </div>
        ) : null}
        <div className="jet-crm-upload__row" style={{ marginTop: '0.5rem' }}>
          <label className="jet-btn jet-btn-secondary jet-crm-upload__btn">
            {form.receiptUrl || form.receiptFile ? 'Replace receipt' : 'Choose file'}
            <input
              type="file"
              accept="image/*,.pdf"
              className="jet-crm-upload__input"
              onChange={(e) => setForm((f) => ({ ...f, receiptFile: e.target.files?.[0] || null }))}
            />
          </label>
        </div>
        {form.receiptFile ? (
          <p className="jet-finance-file-name">{form.receiptFile.name}</p>
        ) : null}
      </div>

      <div className="jet-finance-txn-actions">
        <button type="submit" className="jet-btn jet-btn-primary" disabled={saving}>
          {saving ? 'Saving…' : (isEdit ? 'Update' : `Save ${isExpense ? 'expense' : 'income'}`)}
        </button>
        <button type="button" className="jet-btn jet-btn-secondary" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
    </form>
  );
}
